import { useState, useEffect } from 'react';
import axios from 'axios';
import { fmt, fmtPct } from '../utils/formatters';

import { DETECTOR_LABELS } from './BriefCard';

interface DemoMove {
  detector: string;
  title: string;
  body: string;
  action_label?: string;
  impact_low: number;
  impact_high: number;
  impact_point?: number;
  confidence: number;
  headline_facts?: Record<string, any> | null;
}

interface DemoBrief {
  subject?: string;
  brief_date?: string;
  moves?: DemoMove[];
}

const DEMO_BUSINESS = 'Morning Ritual Coffee Co.';

/**
 * Hardcoded fallback — rendered identically to a live brief whenever the
 * demo endpoint fails or returns null. The landing page must never look broken.
 */
const STATIC_FALLBACK: DemoBrief = {
  subject: 'Three moves worth $1,320–$2,990/mo',
  moves: [
    {
      detector: 'reprice',
      title: 'Cold Brew is priced under its demand',
      body: "Cold Brew sold 412 units last month at $4.25 — 9% below comparable cafés — and demand hasn't dipped once in 12 weeks. Moving to $4.75 keeps it under the Iced Latte and recaptures margin you're already earning.",
      impact_low: 408,
      impact_high: 611,
      confidence: 0.87,
    },
    {
      detector: 'staffing',
      title: 'Your 7–9am rush is outrunning your staffing',
      body: 'Weekday tickets between 7:40 and 8:20am take twice as long to close as your daily average, and the slowdown clusters Tuesday–Thursday. One added bar shift on those mornings covers your three busiest hours.',
      impact_low: 516,
      impact_high: 1290,
      confidence: 0.81,
    },
    {
      detector: 'promote',
      title: 'Americano is quietly your fastest riser',
      body: 'Americano units are up 31% over six weeks with zero promotion — and it carries your second-highest margin. A chalkboard feature and a pastry pairing lock in the momentum before it plateaus.',
      impact_low: 396,
      impact_high: 1089,
      confidence: 0.78,
    },
  ],
};

/** Defensive date formatter — brief_date may be YYYY-MM-DD, ISO, or missing. */
function formatBriefDate(d?: string) {
  if (!d) return '';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T00:00:00`) : new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

export default function SampleBriefCard({ compact = false }: { compact?: boolean }) {
  const [brief, setBrief] = useState<DemoBrief | null>(null);
  const [businessName, setBusinessName] = useState(DEMO_BUSINESS);
  const [live, setLive] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/demo/brief')
      .then((res) => {
        if (cancelled) return;
        const b: DemoBrief | null = res.data?.brief ?? null;
        const validMoves = Array.isArray(b?.moves)
          ? (b!.moves as DemoMove[]).filter((m) => m && m.title && m.body)
          : [];
        if (b && validMoves.length > 0) {
          setBrief({ ...b, moves: validMoves });
          setLive(true);
          if (res.data?.business_name) setBusinessName(res.data.business_name);
        }
      })
      .catch(() => {
        /* fall back silently — STATIC_FALLBACK renders instead */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const cardClass =
    'bg-white border border-[#E6E2DE] rounded-[16px] shadow-[0_8px_40px_rgba(74,45,94,0.10)] ' +
    (compact ? 'p-6' : 'p-6 sm:p-8');

  if (loading) {
    return (
      <div className={cardClass}>
        <div className="animate-pulse space-y-5">
          <div className="h-3 w-56 bg-[#F2F0ED] rounded" />
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 w-2/3 bg-[#F2F0ED] rounded" />
              <div className="h-3 w-full bg-[#F2F0ED] rounded" />
              <div className="h-5 w-44 bg-[#F2F0ED] rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const shown = brief ?? STATIC_FALLBACK;
  const moves = (shown.moves || []).slice(0, 3);
  const dateLabel = live ? formatBriefDate(shown.brief_date) : '';

  return (
    <div className={cardClass}>
      {/* Masthead */}
      <div className="flex items-center justify-between gap-2 border-b border-[#E6E2DE] pb-3 mb-4">
        <span className="font-sans text-[10px] font-semibold uppercase tracking-[0.18em] text-[#4A2D5E]">
          The Morning Brief <span className="font-normal text-[#6B6670]">—</span> {businessName}
        </span>
        {dateLabel && (
          <span className="font-sans text-[10px] text-[#6B6670] whitespace-nowrap hidden sm:inline">
            {dateLabel}
          </span>
        )}
      </div>

      {!compact && shown.subject && (
        <h2 className="font-serif text-xl text-[#2D2A2E] mb-5">{shown.subject}</h2>
      )}

      {/* Moves */}
      <div>
        {moves.map((m, i) => {
          const label = DETECTOR_LABELS[m.detector] || m.detector;
          const oneTime = Boolean(m.headline_facts?.one_time) || m.detector === 'anomaly';
          return (
            <div key={i} className={i > 0 ? 'border-t border-[#F0EDE9] mt-4 pt-4' : ''}>
              <div className="flex items-start gap-3">
                {/* Numbered plum chip */}
                <div
                  className={`rounded-full bg-[#4A2D5E] text-white flex items-center justify-center font-sans font-medium flex-shrink-0 mt-0.5 ${
                    compact ? 'w-6 h-6 text-xs' : 'w-7 h-7 text-sm'
                  }`}
                >
                  {i + 1}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[10px] font-sans font-medium uppercase tracking-widest text-[#4A2D5E]">
                      {label}
                    </div>
                    {!compact && m.confidence > 0 && (
                      <span className="text-[11px] font-sans font-medium px-2 py-0.5 rounded-full bg-[#7BA68E]/15 text-[#2E6B4F] whitespace-nowrap">
                        {fmtPct(Math.round(m.confidence * 100))} confident
                      </span>
                    )}
                  </div>

                  <h3 className={`font-serif text-[#2D2A2E] mt-1 ${compact ? 'text-base' : 'text-lg'}`}>
                    {m.title}
                  </h3>
                  <p
                    className={`font-sans text-sm text-[#6B6670] mt-1 leading-relaxed ${
                      compact ? 'line-clamp-2' : ''
                    }`}
                  >
                    {m.body}
                  </p>

                  {/* Impact line */}
                  <div
                    className={`font-sans font-semibold text-[#2E6B4F] tabular-nums ${
                      compact ? 'text-base mt-2' : 'text-xl mt-3'
                    }`}
                  >
                    worth {fmt(m.impact_low)}–{fmt(m.impact_high)}
                    {oneTime ? ' one-time' : '/mo'}
                    <span className="font-normal text-sm text-[#6B6670]"> — estimated</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Live caption — only when the brief came from the demo endpoint */}
      {live && (
        <div className="flex items-center gap-1.5 mt-5 pt-3 border-t border-[#F0EDE9]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#2E6B4F] animate-pulse" />
          <span className="font-sans text-[11px] text-[#6B6670]">
            Live from our demo café's data
          </span>
        </div>
      )}
    </div>
  );
}
